import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Edit, Trash2, Save, Eye, ChevronRight, ChevronDown,
  GripVertical, ToggleLeft, Mail, Download, Search, Filter,
  CheckCircle, Clock, AlertCircle, X, Copy, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cmsApi } from '../../lib/api';
import { toast } from 'sonner';

// Field type definitions
const FIELD_TYPES = [
  { value: 'text', label: 'Text Input', icon: '📝' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'phone', label: 'Phone', icon: '📞' },
  { value: 'dropdown', label: 'Dropdown', icon: '📋' },
  { value: 'radio', label: 'Radio Buttons', icon: '⭕' },
  { value: 'checkbox', label: 'Checkboxes', icon: '☑️' },
  { value: 'date', label: 'Date Picker', icon: '📅' },
  { value: 'file', label: 'File Upload', icon: '📎' },
  { value: 'hidden', label: 'Hidden Field', icon: '👁️‍🗨️' },
  { value: 'consent', label: 'Consent Checkbox', icon: '✅' },
];

const FormBuilder = () => {
  // View states
  const [view, setView] = useState('list'); // list, edit, submissions
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  
  // Form editing states
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    settings_json: {
      success_message: 'Thank you for your submission!',
      success_message_es: '¡Gracias por su envío!',
      notification_emails: [],
      send_autoresponse: false,
    }
  });
  
  // Field editing states
  const [editingField, setEditingField] = useState(null);
  const [fieldData, setFieldData] = useState({
    field_key: '',
    label: '',
    label_es: '',
    field_type: 'text',
    placeholder: '',
    placeholder_es: '',
    required: false,
    options_json: [],
  });

  // Submission filter
  const [submissionFilter, setSubmissionFilter] = useState('all');

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    setLoading(true);
    try {
      const response = await cmsApi.getForms();
      setForms(response.data || []);
    } catch (error) {
      console.error('Error loading forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFormDetails = async (formId) => {
    try {
      const [formRes, fieldsRes] = await Promise.all([
        cmsApi.getForm(formId),
        cmsApi.getFormFields(formId),
      ]);
      setSelectedForm(formRes.data);
      setFormFields(fieldsRes.data || []);
      setFormData({
        name: formRes.data.name,
        slug: formRes.data.slug,
        description: formRes.data.description || '',
        settings_json: formRes.data.settings_json || {},
      });
    } catch (error) {
      toast.error('Failed to load form details');
    }
  };

  const loadSubmissions = async (formId) => {
    try {
      const response = await cmsApi.getFormSubmissions(formId);
      setSubmissions(response.data || []);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  // ==================== FORM CRUD ====================
  const createForm = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Form name and slug are required');
      return;
    }
    try {
      const response = await cmsApi.createForm(formData);
      toast.success('Form created successfully');
      setSelectedForm(response.data);
      setFormFields([]);
      loadForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create form');
    }
  };

  const updateForm = async () => {
    if (!selectedForm) return;
    try {
      await cmsApi.updateForm(selectedForm.id, formData);
      toast.success('Form updated successfully');
      loadForms();
      loadFormDetails(selectedForm.id);
    } catch (error) {
      toast.error('Failed to update form');
    }
  };

  const publishForm = async () => {
    if (!selectedForm) return;
    try {
      await cmsApi.publishForm(selectedForm.id);
      toast.success('Form published successfully');
      loadFormDetails(selectedForm.id);
      loadForms();
    } catch (error) {
      toast.error('Failed to publish form');
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('Delete this form and all its submissions?')) return;
    try {
      await cmsApi.deleteForm(formId);
      toast.success('Form deleted');
      loadForms();
      if (selectedForm?.id === formId) {
        setSelectedForm(null);
        setView('list');
      }
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  // ==================== FIELD CRUD ====================
  const resetFieldData = () => {
    setFieldData({
      field_key: '',
      label: '',
      label_es: '',
      field_type: 'text',
      placeholder: '',
      placeholder_es: '',
      required: false,
      options_json: [],
    });
    setEditingField(null);
  };

  const saveField = async () => {
    if (!fieldData.field_key || !fieldData.label) {
      toast.error('Field key and label are required');
      return;
    }
    
    try {
      if (editingField && editingField !== 'new') {
        await cmsApi.updateFormField(editingField, fieldData);
        toast.success('Field updated');
      } else {
        await cmsApi.createFormField(selectedForm.id, {
          ...fieldData,
          order_index: formFields.length,
        });
        toast.success('Field added');
      }
      loadFormDetails(selectedForm.id);
      resetFieldData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save field');
    }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;
    try {
      await cmsApi.deleteFormField(fieldId);
      toast.success('Field deleted');
      loadFormDetails(selectedForm.id);
    } catch (error) {
      toast.error('Failed to delete field');
    }
  };

  const startEditField = (field) => {
    setEditingField(field.id);
    setFieldData({
      field_key: field.field_key,
      label: field.label,
      label_es: field.label_es || '',
      field_type: field.field_type,
      placeholder: field.placeholder || '',
      placeholder_es: field.placeholder_es || '',
      required: field.required,
      options_json: field.options_json || [],
    });
  };

  // ==================== SUBMISSIONS ====================
  const updateSubmissionStatus = async (submissionId, status) => {
    try {
      await cmsApi.updateSubmissionStatus(submissionId, status);
      toast.success(`Marked as ${status}`);
      loadSubmissions(selectedForm.id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportSubmissions = async (format) => {
    try {
      const response = await cmsApi.exportSubmissions(selectedForm.id, format);
      // Handle download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedForm.slug}-submissions.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export submissions');
    }
  };

  const filteredSubmissions = submissions.filter(s => 
    submissionFilter === 'all' || s.status === submissionFilter
  );

  // ==================== RENDER ====================
  return (
    <div className="space-y-6" data-testid="form-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Form Builder</h2>
          <p className="text-sm text-[#555555]">Create and manage forms for your website</p>
        </div>
        {view === 'list' && (
          <Button
            onClick={() => {
              setView('edit');
              setSelectedForm(null);
              setFormFields([]);
              setFormData({
                name: '',
                slug: '',
                description: '',
                settings_json: {
                  success_message: 'Thank you for your submission!',
                  success_message_es: '¡Gracias por su envío!',
                  notification_emails: [],
                  send_autoresponse: false,
                }
              });
            }}
            className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
            data-testid="create-form-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Form
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F2A44]"></div>
        </div>
      ) : (
        <>
          {/* Forms List View */}
          {view === 'list' && (
            <div className="bg-white border border-[#E8E4DC]" data-testid="forms-list">
              <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF]">
                <h3 className="font-semibold text-[#222222]">All Forms</h3>
              </div>
              
              {forms.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                  <p className="text-[#555555] mb-2">No forms created yet</p>
                  <p className="text-sm text-[#777777]">Create your first form to start collecting submissions</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E8E4DC]">
                  {forms.map((form) => (
                    <div key={form.id} className="p-4 flex items-center justify-between hover:bg-[#FDFCFA]">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#F5F3EF] rounded flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#1F2A44]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#222222]">{form.name}</p>
                          <p className="text-sm text-[#555555]">/{form.slug} • {form.submission_count || 0} submissions</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          form.status === 'published' ? 'bg-green-100 text-green-800' :
                          form.status === 'disabled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {form.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            loadFormDetails(form.id);
                            loadSubmissions(form.id);
                            setView('submissions');
                          }}
                          data-testid={`view-submissions-${form.slug}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Submissions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            loadFormDetails(form.id);
                            setView('edit');
                          }}
                          data-testid={`edit-form-${form.slug}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteForm(form.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Form Editor View */}
          {view === 'edit' && (
            <div className="space-y-6" data-testid="form-editor">
              {/* Back Button & Actions */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => { setView('list'); setSelectedForm(null); resetFieldData(); }}
                  className="text-[#555555]"
                >
                  <ChevronRight className="w-4 h-4 rotate-180 mr-2" />
                  Back to Forms
                </Button>
                <div className="flex items-center gap-2">
                  {selectedForm && (
                    <>
                      <Badge className={selectedForm.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {selectedForm.status}
                      </Badge>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const baseUrl = window.location.origin;
                          navigator.clipboard.writeText(`${baseUrl}/forms/${selectedForm.slug}`);
                          toast.success('Form URL copied');
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy URL
                      </Button>
                      <Button
                        onClick={publishForm}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Publish
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Form Details */}
              <div className="bg-white border border-[#E8E4DC] p-6">
                <h3 className="font-semibold text-[#222222] mb-4">Form Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Form Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Contact Form"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>URL Slug *</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="contact"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="A brief description of this form..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Form Settings */}
                <div className="mt-6 pt-6 border-t border-[#E8E4DC]">
                  <h4 className="font-medium text-[#222222] mb-4">Form Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Success Message (English)</Label>
                      <Textarea
                        value={formData.settings_json?.success_message || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings_json: { ...formData.settings_json, success_message: e.target.value }
                        })}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Success Message (Spanish)</Label>
                      <Textarea
                        value={formData.settings_json?.success_message_es || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings_json: { ...formData.settings_json, success_message_es: e.target.value }
                        })}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notification Emails (comma-separated)</Label>
                      <Input
                        value={(formData.settings_json?.notification_emails || []).join(', ')}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings_json: {
                            ...formData.settings_json,
                            notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          }
                        })}
                        placeholder="admin@example.com, team@example.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  {selectedForm ? (
                    <Button onClick={updateForm} className="bg-[#1F2A44] text-white">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  ) : (
                    <Button onClick={createForm} className="bg-[#1F2A44] text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Form
                    </Button>
                  )}
                </div>
              </div>

              {/* Form Fields (only show after form is created) */}
              {selectedForm && (
                <div className="bg-white border border-[#E8E4DC]">
                  <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#222222]">Form Fields</h3>
                      <p className="text-sm text-[#555555]">{formFields.length} fields</p>
                    </div>
                    <Button
                      onClick={() => { 
                        console.log('Add Field clicked'); 
                        setFieldData({
                          field_key: '',
                          label: '',
                          label_es: '',
                          field_type: 'text',
                          placeholder: '',
                          placeholder_es: '',
                          required: false,
                          options_json: [],
                        });
                        setEditingField('new'); 
                      }}
                      className="bg-[#C2A46D] hover:bg-[#A8894D] text-white"
                      size="sm"
                      data-testid="add-field-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Field
                    </Button>
                  </div>

                  {/* Field Editor */}
                  {editingField && (
                    <div className="p-4 border-b border-[#E8E4DC] bg-[#FDFCFA]">
                      <h4 className="font-medium text-[#222222] mb-4">
                        {editingField === 'new' ? 'Add New Field' : 'Edit Field'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Field Key *</Label>
                          <Input
                            value={fieldData.field_key}
                            onChange={(e) => setFieldData({ ...fieldData, field_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                            placeholder="full_name"
                            className="mt-1"
                            disabled={editingField !== 'new'}
                          />
                        </div>
                        <div>
                          <Label>Label (English) *</Label>
                          <Input
                            value={fieldData.label}
                            onChange={(e) => setFieldData({ ...fieldData, label: e.target.value })}
                            placeholder="Full Name"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Label (Spanish)</Label>
                          <Input
                            value={fieldData.label_es}
                            onChange={(e) => setFieldData({ ...fieldData, label_es: e.target.value })}
                            placeholder="Nombre Completo"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Field Type</Label>
                          <Select
                            value={fieldData.field_type}
                            onValueChange={(val) => setFieldData({ ...fieldData, field_type: val })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.icon} {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Placeholder (English)</Label>
                          <Input
                            value={fieldData.placeholder}
                            onChange={(e) => setFieldData({ ...fieldData, placeholder: e.target.value })}
                            placeholder="Enter your name..."
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Placeholder (Spanish)</Label>
                          <Input
                            value={fieldData.placeholder_es}
                            onChange={(e) => setFieldData({ ...fieldData, placeholder_es: e.target.value })}
                            placeholder="Ingrese su nombre..."
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Options for dropdown/radio/checkbox */}
                        {['dropdown', 'radio', 'checkbox'].includes(fieldData.field_type) && (
                          <div className="col-span-3">
                            <Label>Options (one per line, format: value|label|label_es)</Label>
                            <Textarea
                              value={(fieldData.options_json || []).map(o => `${o.value}|${o.label}|${o.label_es || ''}`).join('\n')}
                              onChange={(e) => {
                                const options = e.target.value.split('\n').filter(Boolean).map(line => {
                                  const [value, label, label_es] = line.split('|');
                                  return { value: value?.trim(), label: label?.trim() || value?.trim(), label_es: label_es?.trim() };
                                });
                                setFieldData({ ...fieldData, options_json: options });
                              }}
                              placeholder="option1|Option 1|Opción 1&#10;option2|Option 2|Opción 2"
                              className="mt-1 font-mono text-sm"
                              rows={4}
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={fieldData.required}
                            onCheckedChange={(checked) => setFieldData({ ...fieldData, required: checked })}
                          />
                          <Label>Required Field</Label>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button onClick={saveField} className="bg-[#1F2A44] text-white">
                          <Save className="w-4 h-4 mr-2" />
                          {editingField === 'new' ? 'Add Field' : 'Save Field'}
                        </Button>
                        <Button variant="outline" onClick={resetFieldData}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Fields List */}
                  <div className="divide-y divide-[#E8E4DC]">
                    {formFields.length === 0 ? (
                      <div className="p-8 text-center text-[#555555]">
                        No fields added yet. Click "Add Field" to get started.
                      </div>
                    ) : (
                      formFields.map((field, index) => (
                        <div key={field.id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="w-6 h-6 bg-[#F5F3EF] rounded flex items-center justify-center text-sm text-[#555555]">
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium text-[#222222]">{field.label}</p>
                              <div className="flex items-center gap-2 text-sm text-[#555555]">
                                <code className="bg-[#F5F3EF] px-1 rounded">{field.field_key}</code>
                                <Badge className="bg-[#F5F3EF] text-[#555555]">{field.field_type}</Badge>
                                {field.required && <Badge className="bg-red-100 text-red-800">Required</Badge>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditField(field)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteField(field.id)}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submissions View */}
          {view === 'submissions' && selectedForm && (
            <div className="space-y-6" data-testid="form-submissions">
              {/* Back Button & Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    onClick={() => { setView('list'); setSelectedForm(null); }}
                    className="text-[#555555]"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180 mr-2" />
                    Back to Forms
                  </Button>
                  <div>
                    <h3 className="text-xl font-serif text-[#222222]">{selectedForm.name}</h3>
                    <p className="text-sm text-[#555555]">{submissions.length} submissions</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={submissionFilter} onValueChange={setSubmissionFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                      <SelectItem value="spam">Spam</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => exportSubmissions('json')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export JSON
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      loadFormDetails(selectedForm.id);
                      setView('edit');
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Form
                  </Button>
                </div>
              </div>

              {/* Submissions List */}
              <div className="bg-white border border-[#E8E4DC]">
                {filteredSubmissions.length === 0 ? (
                  <div className="p-8 text-center">
                    <Mail className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                    <p className="text-[#555555]">No submissions yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#E8E4DC]">
                    {filteredSubmissions.map((submission) => (
                      <div key={submission.id} className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={
                              submission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                              submission.status === 'read' ? 'bg-gray-100 text-gray-800' :
                              submission.status === 'spam' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-600'
                            }>
                              {submission.status}
                            </Badge>
                            <span className="text-sm text-[#555555]">
                              {new Date(submission.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {submission.status === 'new' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateSubmissionStatus(submission.id, 'read')}
                              >
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateSubmissionStatus(submission.id, 'archived')}
                            >
                              <X className="w-4 h-4 text-gray-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateSubmissionStatus(submission.id, 'spam')}
                            >
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-[#F5F3EF] p-3 rounded">
                          {Object.entries(submission.data_json || {}).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-xs text-[#777777] capitalize">{key.replace(/_/g, ' ')}:</span>
                              <p className="text-sm text-[#222222]">{String(value)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FormBuilder;
